import { doc, updateDoc } from 'firebase/firestore';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Pill, 
  SmilePlus, 
  Frown, 
  Phone, 
  Calendar, 
  ListChecks, 
  AlertCircle,
  X,
  CheckCircle,
  LucideIcon
} from 'lucide-react';
import { useAppState } from '@/contexts/AppStateContext';
import { useElderCare } from '@/contexts/ElderCareContext';
import { db } from '@/firebase';

const getTodayDate = () => new Date().toISOString().split('T')[0];

// Reusable Tile Component
const Tile: React.FC<{ icon: LucideIcon; label: string; color: string; onClick?: () => void; children?: React.ReactNode }> = ({ icon: Icon, label, color, onClick, children }) => (
  <button 
    onClick={onClick}
    className={`${color} rounded-3xl p-6 text-white text-left min-h-[120px] flex flex-col gap-3 active:scale-95 transition-transform shadow-sm`}
  >
    <Icon className="w-8 h-8" />
    <span className="text-lg font-black leading-tight">{label}</span>
    {children}
  </button>
);

// Medication Confirmation Sheet
const MedConfirmSheet: React.FC<{ isOpen: boolean; onClose: () => void; profileId: string }> = ({ isOpen, onClose, profileId }) => {
  const { allMedications, callElderCare } = useElderCare();
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const medications = allMedications.get(profileId) ?? [];

  const handleMarkTaken = async (med: { id: string; name: string; dosage: string; scheduledTimes?: string[] }) => {
    if (loadingIds.has(med.id)) return;
    
    setLoadingIds(prev => new Set(prev).add(med.id));
    
    try {
      await callElderCare('logAdherence', {
        profileId,
        medicationId: med.id,
        scheduledDate: getTodayDate(),
        status: 'taken',
        takenAt: new Date().toISOString()
      });
      
      // success logged silently
    } catch (error) {
      console.error('Error logging adherence:', error);
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(med.id);
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Take Medication</h2>
          <button 
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          {medications.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No medications due today</p>
            </div>
          ) : (
            <div className="space-y-4">
              {medications.map(med => (
                <div key={med.id} className="border dark:border-slate-800 rounded-2xl p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">{med.name}</h3>
                      <p className="text-slate-500 dark:text-slate-400">{med.dosage} • {med.scheduledTimes?.[0]}</p>
                    </div>
                    <button
                      onClick={() => handleMarkTaken(med)}
                      disabled={loadingIds.has(med.id)}
                      className={`px-4 py-2 rounded-xl text-white font-medium transition-colors ${loadingIds.has(med.id) ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-brand active:scale-95'}`}
                    >
                      {loadingIds.has(med.id) ? '...' : 'Mark Taken'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
          <button 
            onClick={onClose}
            className="w-full py-3 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-200 dark:hover:bg-slate-900 rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Help Contact Modal
const HelpContactModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full p-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Contact Your Care Circle</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6">
          For urgent assistance, please contact your care circle member by phone. 
          You can find their contact information in your profile or ask a caregiver.
        </p>
        <button 
          onClick={onClose}
          className="w-full py-3 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Elder Home Screen
export default function ElderHomeScreen() {
  const { state, dispatch } = useAppState();
  const { t } = useTranslation();
  const { careProfiles, allMedications, callElderCare, getUpcomingAppointments } = useElderCare();
  const profile = careProfiles[0];
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const showToast = ({ message }: { message: string }) => { setToastMsg(message); setTimeout(() => setToastMsg(null), 3000); };
  
  // Local state
  const [showMedSheet, setShowMedSheet] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [checkInStep, setCheckInStep] = useState(0);
  const [checkInData, setCheckInData] = useState({
    painLevel: 0,
    generalFeeling: '',
    needsHelp: false
  });

  // Helper for confirmations
  const showConfirm = (message: string, onConfirm: () => void) => {
    showToast({ message });
    onConfirm();
  };

  const handleMedicationTaken = () => {
    setShowMedSheet(true);
  };

  const handleCheckInOkay = () => {
    showConfirm(
      "Great! Your care circle has been notified.",
      () => void callElderCare('logCheckIn', {
        profileId: profile?.id ?? '',
        date: getTodayDate(),
        painLevel: 0,
        generalFeeling: 'same',
        needsHelp: false
      })
    );
  };

  const handleCheckInNotWellStart = () => {
    setCheckInStep(1);
  };

  const handleCheckInPain = (level: 0 | 1 | 2) => {
    setCheckInData(prev => ({ ...prev, painLevel: level }));
    setCheckInStep(2);
  };

  const handleCheckInFeeling = (feeling: 'same' | 'worse' | 'better') => {
    setCheckInData(prev => ({ ...prev, generalFeeling: feeling }));
    setCheckInStep(3);
  };

  const handleCheckInHelp = (needsHelp: boolean) => {
    setCheckInData(prev => ({ ...prev, needsHelp }));
    
    const { painLevel, generalFeeling } = checkInData;
    
    if (needsHelp) {
      showConfirm(
        "Help is on the way. Your care circle has been notified.",
        () => callElderCare('logCheckIn', {
          profileId: profile.id,
          date: getTodayDate(),
          painLevel,
          generalFeeling,
          needsHelp: true
        })
      );
    } else {
      showConfirm(
        "Understood. Your care circle has been notified.",
        () => callElderCare('logCheckIn', {
          profileId: profile.id,
          date: getTodayDate(),
          painLevel,
          generalFeeling,
          needsHelp: false
        })
      );
    }
    
    setCheckInStep(0);
    setCheckInData({ painLevel: 0, generalFeeling: '', needsHelp: false });
  };

  const handleExitElderMode = async () => {
    if (window.confirm("Switch to Standard View?")) {
      try {
        if (!state.currentUser?.id) return;
        await updateDoc(doc(db, 'users', state.currentUser.id), {
          elderModeEnabled: false
        });
        
        // Dispatch update to state
        // Assuming AppStateContext has an action for this
        // For this implementation, we'll assume a custom hook or context method exists
        // Since it's not specified, we use a workaround with state
        dispatch({ type: 'UPDATE_USER', payload: { elderModeEnabled: false } } as any);
        showToast({ message: 'Exited Elder Mode' });
      } catch (error) {
        console.error('Error exiting elder mode:', error);
        showToast({ message: 'Error exiting Elder Mode' });
      }
    }
  };

  const getCheckInStepContent = () => {
    if (checkInStep === 1) {
      return (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">How is your pain?</h3>
          <div className="grid grid-cols-1 gap-4">
            <button 
              onClick={() => handleCheckInPain(0)}
              className="py-4 px-6 bg-slate-100 dark:bg-slate-800 rounded-2xl text-lg font-bold text-slate-800 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
            >
              None
            </button>
            <button 
              onClick={() => handleCheckInPain(1)}
              className="py-4 px-6 bg-slate-100 dark:bg-slate-800 rounded-2xl text-lg font-bold text-slate-800 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
            >
              A Little
            </button>
            <button 
              onClick={() => handleCheckInPain(2)}
              className="py-4 px-6 bg-slate-100 dark:bg-slate-800 rounded-2xl text-lg font-bold text-slate-800 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
            >
              A Lot
            </button>
          </div>
        </div>
      );
    }
    
    if (checkInStep === 2) {
      return (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">How do you feel overall?</h3>
          <div className="grid grid-cols-1 gap-4">
            <button 
              onClick={() => handleCheckInFeeling('same')}
              className="py-4 px-6 bg-slate-100 dark:bg-slate-800 rounded-2xl text-lg font-bold text-slate-800 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
            >
              Same as Usual
            </button>
            <button 
              onClick={() => handleCheckInFeeling('worse')}
              className="py-4 px-6 bg-slate-100 dark:bg-slate-800 rounded-2xl text-lg font-bold text-slate-800 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
            >
              Worse Than Usual
            </button>
          </div>
        </div>
      );
    }
    
    if (checkInStep === 3) {
      return (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Do you need help right now?</h3>
          <div className="grid grid-cols-1 gap-4">
            <button 
              onClick={() => handleCheckInHelp(false)}
              className="py-4 px-6 bg-slate-100 dark:bg-slate-800 rounded-2xl text-lg font-bold text-slate-800 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 active:scale-95 transition-transform"
            >
              No, just logging
            </button>
            <button 
              onClick={() => handleCheckInHelp(true)}
              className="py-4 px-6 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl text-lg font-bold text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 active:scale-95 transition-transform"
            >
              Yes, contact someone
            </button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const upcomingAppointment = profile ? getUpcomingAppointments(profile.id, 30)[0] : undefined;
  const activeMedications = profile ? (allMedications.get(profile.id) ?? []).filter(m => !m.endDate || new Date(m.endDate) >= new Date()) : [];

  return (
    <>
      <div className="min-h-screen bg-white dark:bg-slate-950 p-6">
        <div className="max-w-lg mx-auto">
          <h1 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{t('elder.good_morning', 'Good morning')}</h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 mb-8">{profile?.firstName ? `${profile.firstName}'s ${t('elder.care', 'Care')}` : t('elder.title', 'Elder Care')}</p>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Tile 1: Medication */}
            <Tile
              icon={Pill}
              label={t('elder.took_medication', 'I Took My Medication')}
              color="bg-blue-500 hover:bg-brand"
              onClick={handleMedicationTaken}
            />

            {/* Tile 2: Check In Okay */}
            <Tile
              icon={SmilePlus}
              label={t('elder.doing_okay', "I'm Doing Okay")}
              color="bg-green-500 hover:bg-green-600"
              onClick={handleCheckInOkay}
            />

            {/* Tile 3: Not Feeling Well */}
            <Tile
              icon={Frown}
              label={t('elder.not_well', "I Don't Feel Well")}
              color="bg-orange-500 hover:bg-orange-600"
              onClick={handleCheckInNotWellStart}
            />

            {/* Tile 4: Call for Help */}
            <Tile
              icon={Phone}
              label={t('elder.call_help', 'Call for Help')}
              color="bg-red-500 hover:bg-red-600"
              onClick={() => setShowHelpModal(true)}
            />
            
            {/* Tile 5: Appointment */}
            <Tile
              icon={Calendar}
              label={t('elder.next_appointment', 'My Next Appointment')}
              color="bg-indigo-500 hover:bg-brand"
            >
              {upcomingAppointment ? (
                <div className="mt-1 bg-white/20 p-2 rounded-xl">
                  <p className="font-bold">{upcomingAppointment.title}</p>
                  <p className="text-sm opacity-90">
                    {new Date(upcomingAppointment.dateTime).toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })} at {new Date(upcomingAppointment.dateTime).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              ) : (
                <p className="mt-1 text-sm opacity-90 bg-white/20 px-2 py-1 rounded-xl">
                  {t('elder.no_upcoming_appointments', 'No upcoming appointments')}
                </p>
              )}
            </Tile>
            
            {/* Tile 6: Medications List */}
            <Tile 
              icon={ListChecks} 
              label="My Medications" 
              color="bg-slate-500 hover:bg-slate-600"
            >
              {activeMedications.length > 0 ? (
                <div className="mt-1 space-y-1">
                  {activeMedications.slice(0, 2).map(med => (
                    <div key={med.id} className="bg-white/20 px-2 py-1 rounded-lg text-sm">
                      <p className="font-medium">{med.name}</p>
                      <p className="opacity-80">{med.dosage} • {med.scheduledTimes?.[0] ?? ''}</p>
                    </div>
                  ))}
                  {activeMedications.length > 2 && (
                    <p className="text-xs opacity-75">+ {activeMedications.length - 2} more</p>
                  )}
                </div>
              ) : (
                <p className="mt-1 text-sm opacity-90 bg-white/20 px-2 py-1 rounded-xl">
                  No active medications
                </p>
              )}
            </Tile>
          </div>
          
          {/* Check-in step modal (outside button elements — valid HTML) */}
          {checkInStep > 0 && checkInStep < 4 && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
              <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-sm w-full p-6 shadow-2xl">
                {getCheckInStepContent()}
                <button
                  onClick={() => setCheckInStep(0)}
                  className="mt-4 w-full py-3 text-slate-500 dark:text-slate-400 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Exit Elder Mode button */}
          <div className="mt-12 flex justify-center">
            <button 
              onClick={handleExitElderMode}
              className="px-6 py-3 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-medium hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
            >
              Standard View
            </button>
          </div>
        </div>
      </div>
      
      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-lg z-50 text-sm font-medium">
          {toastMsg}
        </div>
      )}
      
      <MedConfirmSheet 
        isOpen={showMedSheet} 
        onClose={() => setShowMedSheet(false)}
        profileId={profile?.id ?? ''}
      />
      
      <HelpContactModal 
        isOpen={showHelpModal} 
        onClose={() => setShowHelpModal(false)}
      />
    </>
  );
}